import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Wallet, CheckCircle2, AlertCircle } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { KpiCard } from '@/components/ui/KpiCard';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/form/Select';
import { SearchableSelect, type SearchableOption } from '@/components/form/SearchableSelect';
import { useToast } from '@/components/ui/useToast';
import { usePrompt } from '@/components/ui/usePrompt';
import { apiError } from '@/lib/apiError';
import { formatINR, formatDate } from '@/lib/format';
import { listSisStores } from '@/api/sisStock';
import { listSisSettlements, closeSisSettlement, type SisSettlement } from '@/api/sisSettlement';
import { GenerateSettlementModal } from './GenerateSettlementModal';
import { ReceivePaymentModal } from './ReceivePaymentModal';

const STATUS_TONE: Record<string, 'warning' | 'info' | 'success' | 'muted'> = {
  Pending: 'warning',
  Partial: 'info',
  Paid: 'success',
  Closed: 'muted',
};

export function SisSettlementPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const ask = usePrompt();
  const [partyId, setPartyId] = useState('');
  const [status, setStatus] = useState('');
  const [genOpen, setGenOpen] = useState(false);
  const [payRow, setPayRow] = useState<SisSettlement | null>(null);

  const filter = useMemo(
    () => ({ partyId: partyId ? Number(partyId) : undefined, status: status || undefined }),
    [partyId, status],
  );
  const storesQ = useQuery({ queryKey: ['sis-stores'], queryFn: () => listSisStores(), staleTime: 0 });
  const query = useQuery({
    queryKey: ['sis-settlements', filter],
    queryFn: () => listSisSettlements(filter),
    refetchInterval: 60_000,
  });
  const rows = query.data ?? [];

  const summary = useMemo(() => {
    let expected = 0, received = 0, outstanding = 0;
    for (const r of rows) {
      expected += r.finalReceivable;
      received += r.received;
      if (r.status !== 'Closed') outstanding += r.balance;
    }
    return { expected, received, outstanding };
  }, [rows]);

  const storeOptions = useMemo<SearchableOption[]>(
    () => [
      { value: '', label: 'All Stores' },
      ...(storesQ.data ?? []).map((s) => ({ value: String(s.id), label: s.name, sublabel: s.city || undefined })),
    ],
    [storesQ.data],
  );

  const closeM = useMutation({
    mutationFn: ({ id, note }: { id: number; note?: string }) => closeSisSettlement(id, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sis-settlements'] });
      toast.push('Settlement closed', 'success');
    },
    onError: (e) => toast.push(apiError(e, 'Could not close settlement'), 'error'),
  });

  async function onClose(r: SisSettlement) {
    const variance = Math.round((r.netReceivable - r.received) * 100) / 100;
    const note = await ask({
      title: `Close ${r.settlementNo}?`,
      message: `Variance (expected − received) = ₹${formatINR(variance)}.`,
      label: 'Note',
      placeholder: 'Optional — e.g. approved deduction',
      multiline: true,
      required: false,
      confirmLabel: 'Close settlement',
    });
    if (note === null) return; // cancelled
    closeM.mutate({ id: r.id, note: note || undefined });
  }

  return (
    <div>
      <SectionHeader
        title="SIS Settlement"      />

      {/* Reconciliation summary */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KpiCard label="Expected (net receivable)" value={`₹${formatINR(summary.expected)}`} accent="#6366f1" Icon={Wallet} />
        <KpiCard label="Received" value={`₹${formatINR(summary.received)}`} accent="#10b981" Icon={CheckCircle2} />
        <KpiCard label="Outstanding" value={`₹${formatINR(summary.outstanding)}`} accent="#ef4444" Icon={AlertCircle} />
      </div>

      <FilterBar>
        <FilterField label="Store">
          <SearchableSelect options={storeOptions} value={partyId} onChange={(v) => setPartyId(v === '' ? '' : String(v))} placeholder="Type a store name…" />
        </FilterField>
        <FilterField label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option>Pending</option>
            <option>Partial</option>
            <option>Paid</option>
            <option>Closed</option>
          </Select>
        </FilterField>
        <div className="col-span-3 sm:col-span-1 sm:ml-auto sm:self-end">
          <Button variant="primary" size="sm" icon={<Plus size={12} />} onClick={() => setGenOpen(true)} className="w-full justify-center sm:w-auto">
            Generate settlement
          </Button>
        </div>
      </FilterBar>

      <Table<SisSettlement>
        storageKey="sis-settlements"
        paginated
        title="Settlements"
        count={`${rows.length} settlements`}
        rows={rows}
        rowKey={(r) => r.id}
        loading={query.isFetching && rows.length === 0}
        columns={[
          { key: 'no', header: 'Settlement', cell: (r) => <span className="font-extrabold text-accent">{r.settlementNo}</span> },
          { key: 'store', header: 'Store', cell: (r) => <span className="font-bold">{r.storeName}</span> },
          { key: 'period', header: 'Period', cell: (r) => `${formatDate(r.periodStart)} – ${formatDate(r.periodEnd)}` },
          { key: 'gross', header: 'Gross', align: 'right', cell: (r) => `₹${formatINR(r.grossSale)}` },
          { key: 'comm', header: 'Commission', align: 'right', cell: (r) => `${r.commissionPct}%` },
          { key: 'net', header: 'Net Receivable', align: 'right', cell: (r) => `₹${formatINR(r.netReceivable)}` },
          { key: 'salary', header: 'Salary', align: 'right', cell: (r) => (r.salaryDeduction ? `₹${formatINR(r.salaryDeduction)}` : '—') },
          { key: 'tds', header: 'TDS', align: 'right', cell: (r) => (r.tdsAmount ? `₹${formatINR(r.tdsAmount)}` : '—') },
          { key: 'final', header: 'Final', align: 'right', cell: (r) => <strong className="text-accent">₹{formatINR(r.finalReceivable)}</strong> },
          { key: 'recd', header: 'Received', align: 'right', cell: (r) => `₹${formatINR(r.received)}` },
          {
            key: 'bal',
            header: 'Balance',
            align: 'right',
            cell: (r) => (r.balance > 0 ? <span className="font-bold text-danger">₹{formatINR(r.balance)}</span> : `₹${formatINR(r.balance)}`),
          },
          { key: 'status', header: 'Status', cell: (r) => <Badge tone={STATUS_TONE[r.status] ?? 'muted'}>{r.status}</Badge> },
          {
            key: 'actions',
            header: '',
            align: 'right',
            cell: (r) =>
              r.status === 'Closed' ? (
                <span className="text-xs text-[var(--text-muted)]">—</span>
              ) : (
                <span className="inline-flex gap-1">
                  {r.status !== 'Paid' && (
                    <Button size="sm" variant="glass" onClick={() => setPayRow(r)}>Receive</Button>
                  )}
                  <Button size="sm" variant="glass" disabled={closeM.isPending} onClick={() => onClose(r)}>Close</Button>
                </span>
              ),
          },
        ]}
      />

      <GenerateSettlementModal open={genOpen} onClose={() => setGenOpen(false)} />
      <ReceivePaymentModal open={!!payRow} settlement={payRow} onClose={() => setPayRow(null)} />
    </div>
  );
}
